import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Save, Phone, MapPin, Clock, Link as LinkIcon } from 'lucide-react';

const AdminCMSRestaurantInfo = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const autoSaveTimer = useRef(null);
  
  const [form, setForm] = useState({
    phone: '',
    address: '',
    opening_hours: '',
    reservation_link: ''
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchInfo();
  }, []);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (hasChanges) {
      autoSaveTimer.current = setTimeout(() => {
        handleAutoSave();
      }, 30000);
    }
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [form, hasChanges]);

  const fetchInfo = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/restaurant-info`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setForm(prev => ({ ...prev, ...data }));
        }
      }
    } catch (error) {
      console.log('No existing info, starting fresh');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/restaurant-info`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setHasChanges(false);
      toast({ title: "Brouillon sauvegardé", description: "Auto-save" });
    } catch (error) {
      console.error('Auto-save failed');
    }
  };

  const handleSave = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/restaurant-info`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        setHasChanges(false);
        toast({ title: "Succès", description: "Informations enregistrées" });
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de sauvegarder", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/cms" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Informations du Restaurant</h1>
              <p className="text-sm text-gray-500">Coordonnées et horaires</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <span className="text-sm text-amber-600">Modifications non sauvegardées</span>
            )}
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
            >
              <Save size={18} />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Phone */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Phone className="text-gray-600" size={20} />
            <h2 className="text-lg font-bold text-gray-900">Téléphone</h2>
          </div>
          <Input
            value={form.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder="04 50 20 XX XX"
            data-testid="restaurant-phone"
          />
        </div>

        {/* Address */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="text-gray-600" size={20} />
            <h2 className="text-lg font-bold text-gray-900">Adresse</h2>
          </div>
          <Textarea
            value={form.address}
            onChange={(e) => updateField('address', e.target.value)}
            placeholder="123 Avenue des Thermes&#10;01220 Divonne-les-Bains"
            rows={3}
            data-testid="restaurant-address"
          />
        </div>

        {/* Opening Hours */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="text-gray-600" size={20} />
            <h2 className="text-lg font-bold text-gray-900">Horaires d'ouverture</h2>
          </div>
          <Textarea
            value={form.opening_hours}
            onChange={(e) => updateField('opening_hours', e.target.value)}
            placeholder="Mardi - Samedi : 12h-14h / 19h-22h&#10;Dimanche : 12h-14h&#10;Fermé le lundi"
            rows={4}
            data-testid="restaurant-hours"
          />
          <p className="text-xs text-gray-500 mt-2">
            Utilisez une nouvelle ligne pour chaque jour ou groupe de jours
          </p>
        </div>

        {/* Reservation Link */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon className="text-gray-600" size={20} />
            <h2 className="text-lg font-bold text-gray-900">Lien de réservation</h2>
          </div>
          <Input
            value={form.reservation_link}
            onChange={(e) => updateField('reservation_link', e.target.value)}
            placeholder="https://..."
            data-testid="restaurant-reservation-link"
          />
          <p className="text-xs text-gray-500 mt-2">
            Lien externe vers votre système de réservation (optionnel)
          </p>
        </div>

        {/* Auto-save info */}
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Auto-save :</strong> Vos modifications sont sauvegardées automatiquement en brouillon toutes les 30 secondes.
          </p>
        </div>

        {/* Save Button (mobile) */}
        <div className="md:hidden mt-6">
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="w-full bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2"
          >
            <Save size={18} />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminCMSRestaurantInfo;
